/**
 * Perfect mazes on any cell graph: rectangular grids and circular (theta) mazes.
 *
 * A perfect maze is a spanning tree of the cell graph — every cell can be reached and
 * there is exactly one route between any two cells, so no loops and no sealed rooms. Two
 * generators are offered, and both work on any graph that lists each cell's neighbours:
 *
 * - Recursive backtracker (depth-first search): long, winding corridors and few dead
 *   ends. Called "winding" in the widget.
 * - Wilson's algorithm (loop-erased random walks): picks uniformly among every possible
 *   perfect maze of the graph, with many short dead ends. Called "branching".
 *
 * All randomness comes from the seeded `Rng` passed in, so a maze is a pure function of
 * (shape, size, style, seed).
 */

import {
  createRng,
  type Rng,
  randInt,
  shuffled,
} from "@/lib/tool-kit/worksheets/random";

export type MazeShape = "rectangular" | "circular";
export type MazeStyle = "winding" | "branching";
export type MazeLevel = "easy" | "medium" | "hard" | "extreme";

export const MAZE_LEVELS: readonly MazeLevel[] = [
  "easy",
  "medium",
  "hard",
  "extreme",
];

/** Preset sizes: columns × rows for a rectangle, rings for a circle. */
export const MAZE_PRESETS: Record<
  MazeLevel,
  { cols: number; rows: number; rings: number }
> = {
  easy: { cols: 10, rows: 10, rings: 5 },
  medium: { cols: 18, rows: 18, rings: 8 },
  hard: { cols: 28, rows: 28, rings: 12 },
  extreme: { cols: 40, rows: 40, rings: 18 },
};

export const MIN_SIDE = 3;
export const MAX_SIDE = 60;
export const MIN_RINGS = 3;
export const MAX_RINGS = 24;

export interface MazeGraph {
  shape: MazeShape;
  cells: number;
  neighbors: number[][];
  /** Rectangular: columns and rows. */
  cols: number;
  rows: number;
  /** Circular: how many cells each ring has (ring 0 is the single centre cell). */
  ringCounts: number[];
  /** Circular: id of each ring's first cell. */
  ringStart: number[];
}

export interface Maze {
  graph: MazeGraph;
  /** For each cell, the neighbours it is joined to by an opening. */
  links: number[][];
  entrance: number;
  exit: number;
  style: MazeStyle;
  seed: number;
}

export interface MazeStats {
  cells: number;
  /** Cells on the shortest (and only) route from entrance to exit, both included. */
  solutionLength: number;
  /** Cells with a single opening, not counting the entrance and the exit. */
  deadEnds: number;
  /** Cells on the route where the solver can turn off down a side passage. */
  decisionPoints: number;
  /** Cells the solution does not touch. */
  offRoute: number;
}

export function clampSide(value: number): number {
  return Math.min(MAX_SIDE, Math.max(MIN_SIDE, Math.round(value) || MIN_SIDE));
}

export function clampRings(value: number): number {
  return Math.min(
    MAX_RINGS,
    Math.max(MIN_RINGS, Math.round(value) || MIN_RINGS)
  );
}

/* ------------------------------------------------------------------- graphs */

export function rectangularGraph(cols: number, rows: number): MazeGraph {
  const neighbors: number[][] = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const list: number[] = [];
      if (r > 0) {
        list.push((r - 1) * cols + c);
      }
      if (c < cols - 1) {
        list.push(r * cols + c + 1);
      }
      if (r < rows - 1) {
        list.push((r + 1) * cols + c);
      }
      if (c > 0) {
        list.push(r * cols + c - 1);
      }
      neighbors.push(list);
    }
  }
  return {
    shape: "rectangular",
    cells: cols * rows,
    neighbors,
    cols,
    rows,
    ringCounts: [],
    ringStart: [],
  };
}

/**
 * Cells per ring for a circular maze: one at the centre, six in the first ring, and then
 * each ring multiplies the one inside it by the whole number that keeps its cells closest
 * to square. The rule is Jamis Buck's "theta maze" layout.
 */
export function ringCountsFor(rings: number): number[] {
  const counts = [1];
  for (let ring = 1; ring < rings; ring += 1) {
    const previous = counts[ring - 1] ?? 1;
    const width = (2 * Math.PI * ring) / previous;
    counts.push(previous * Math.max(1, Math.round(width)));
  }
  return counts;
}

export function circularGraph(rings: number): MazeGraph {
  const ringCounts = ringCountsFor(rings);
  const ringStart: number[] = [];
  let total = 0;
  for (const count of ringCounts) {
    ringStart.push(total);
    total += count;
  }
  const neighbors: number[][] = Array.from({ length: total }, () => []);
  const join = (a: number, b: number) => {
    neighbors[a]?.push(b);
    neighbors[b]?.push(a);
  };
  for (let ring = 1; ring < rings; ring += 1) {
    const count = ringCounts[ring] ?? 1;
    const inner = ringCounts[ring - 1] ?? 1;
    const start = ringStart[ring] ?? 0;
    const innerStart = ringStart[ring - 1] ?? 0;
    const ratio = count / inner;
    for (let j = 0; j < count; j += 1) {
      join(start + j, start + ((j + 1) % count));
      join(start + j, innerStart + Math.floor(j / ratio));
    }
  }
  return {
    shape: "circular",
    cells: total,
    neighbors,
    cols: 0,
    rows: 0,
    ringCounts,
    ringStart,
  };
}

/** The ring a circular-maze cell is in, and its position round that ring. */
export function ringOf(
  graph: MazeGraph,
  cell: number
): { ring: number; index: number } {
  let ring = graph.ringStart.length - 1;
  while (ring > 0 && (graph.ringStart[ring] ?? 0) > cell) {
    ring -= 1;
  }
  return { ring, index: cell - (graph.ringStart[ring] ?? 0) };
}

/* -------------------------------------------------------------- generators */

function emptyLinks(cells: number): number[][] {
  return Array.from({ length: cells }, () => []);
}

function connect(links: number[][], a: number, b: number): void {
  links[a]?.push(b);
  links[b]?.push(a);
}

/** Recursive backtracker, run with an explicit stack so a big maze cannot overflow. */
export function carveWinding(graph: MazeGraph, rng: Rng): number[][] {
  const links = emptyLinks(graph.cells);
  const visited = new Array<boolean>(graph.cells).fill(false);
  const first = randInt(rng, 0, graph.cells - 1);
  const stack = [first];
  visited[first] = true;
  while (stack.length > 0) {
    const cell = stack.at(-1) as number;
    const open = (graph.neighbors[cell] ?? []).filter((next) => !visited[next]);
    if (open.length === 0) {
      stack.pop();
    } else {
      const next = open[randInt(rng, 0, open.length - 1)] as number;
      connect(links, cell, next);
      visited[next] = true;
      stack.push(next);
    }
  }
  return links;
}

/**
 * Wilson's algorithm: from each cell not yet in the maze, walk at random until the walk
 * meets the maze, erasing any loop the walk makes, then add the loop-free path. Every
 * perfect maze of the graph is equally likely.
 */
export function carveBranching(graph: MazeGraph, rng: Rng): number[][] {
  const links = emptyLinks(graph.cells);
  const inMaze = new Array<boolean>(graph.cells).fill(false);
  const next = new Array<number>(graph.cells).fill(-1);
  inMaze[randInt(rng, 0, graph.cells - 1)] = true;
  for (const start of shuffled(
    rng,
    Array.from({ length: graph.cells }, (_, i) => i)
  )) {
    let cell = start;
    while (!inMaze[cell]) {
      const options = graph.neighbors[cell] ?? [];
      next[cell] = options[randInt(rng, 0, options.length - 1)] as number;
      cell = next[cell] as number;
    }
    cell = start;
    while (!inMaze[cell]) {
      inMaze[cell] = true;
      connect(links, cell, next[cell] as number);
      cell = next[cell] as number;
    }
  }
  return links;
}

/* ----------------------------------------------------------------- solving */

/** The shortest route between two cells: the only route, in a perfect maze. */
export function solveMaze(
  links: readonly (readonly number[])[],
  from: number,
  to: number
): number[] {
  const parent = new Map<number, number>([[from, -1]]);
  const queue = [from];
  for (let head = 0; head < queue.length && !parent.has(to); head += 1) {
    const cell = queue[head] as number;
    for (const next of links[cell] ?? []) {
      if (!parent.has(next)) {
        parent.set(next, cell);
        queue.push(next);
      }
    }
  }
  if (!parent.has(to)) {
    return [];
  }
  const path: number[] = [];
  for (let cell = to; cell !== -1; cell = parent.get(cell) as number) {
    path.push(cell);
  }
  return path.reverse();
}

export function solution(maze: Maze): number[] {
  return solveMaze(maze.links, maze.entrance, maze.exit);
}

export function mazeStats(maze: Maze): MazeStats {
  const path = solution(maze);
  const onRoute = new Set(path);
  let deadEnds = 0;
  for (let cell = 0; cell < maze.graph.cells; cell += 1) {
    const isEnd = cell === maze.entrance || cell === maze.exit;
    if (!isEnd && maze.links[cell]?.length === 1) {
      deadEnds += 1;
    }
  }
  const decisionPoints = path
    .slice(1, -1)
    .filter((cell) => (maze.links[cell]?.length ?? 0) >= 3).length;
  return {
    cells: maze.graph.cells,
    solutionLength: path.length,
    deadEnds,
    decisionPoints,
    offRoute: maze.graph.cells - onRoute.size,
  };
}

/* ---------------------------------------------------------------- building */

export interface MazeRequest {
  shape: MazeShape;
  style: MazeStyle;
  cols: number;
  rows: number;
  rings: number;
  seed: number;
}

/**
 * Build a maze. A rectangular maze opens in the top-left corner and leaves at the
 * bottom-right; a circular maze opens on the outer ring and finishes at the centre.
 */
export function generateMaze(request: MazeRequest): Maze {
  const rng = createRng(request.seed);
  const circular = request.shape === "circular";
  const graph = circular
    ? circularGraph(clampRings(request.rings))
    : rectangularGraph(clampSide(request.cols), clampSide(request.rows));
  const links =
    request.style === "winding"
      ? carveWinding(graph, rng)
      : carveBranching(graph, rng);
  const lastRing = graph.ringStart.at(-1) ?? 0;
  return {
    graph,
    links,
    entrance: circular ? lastRing : 0,
    exit: circular ? 0 : graph.cells - 1,
    style: request.style,
    seed: request.seed,
  };
}

/** True when the links form a spanning tree: connected, with cells − 1 openings. */
export function isPerfect(maze: Maze): boolean {
  const { cells } = maze.graph;
  const openings = maze.links.reduce((sum, list) => sum + list.length, 0) / 2;
  if (openings !== cells - 1) {
    return false;
  }
  const seen = new Set<number>([0]);
  const queue = [0];
  for (const cell of queue) {
    for (const next of maze.links[cell] ?? []) {
      if (!seen.has(next)) {
        seen.add(next);
        queue.push(next);
      }
    }
  }
  return seen.size === cells;
}
